import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeft, User, Phone, Mail, MapPin, Building2,
    ShoppingBag, CreditCard, Star, Calendar, Edit, Package
} from 'lucide-react';
import { customersAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface RecentOrder {
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    paidAmount: number;
    paymentStatus: string;
    createdAt: string;
    dueDate?: string;
}

interface CustomerData {
    _id: string;
    firstName: string;
    lastName: string;
    nickname?: string;
    email: string;
    phone: string;
    address?: string;
    postcode?: string;
    city?: string;
    dateOfBirth?: string;
    isBusiness: boolean;
    businessName?: string;
    vatNumber?: string;
    accountNumber?: string;
    notes?: string;
    isActive: boolean;
    loyaltyPoints: number;
    totalOrders: number;
    totalSpent: number;
    lastOrderDate?: string;
    creditLimit: number;
    currentBalance: number;
    gdprConsent: boolean;
    marketingConsent: boolean;
    preferredNotificationMethod: string;
    recentOrders: RecentOrder[];
    createdAt: string;
}

const statusColors: Record<string, string> = {
    received: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    ready: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
};

const paymentStatusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    partial: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-red-100 text-red-800',
};

const CustomerDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<CustomerData | null>(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        nickname: '',
        email: '',
        phone: '',
        address: '',
        postcode: '',
        city: '',
        isBusiness: false,
        notes: '',
        gdprConsent: false,
        marketingConsent: false,
        preferredNotificationMethod: 'email',
    });

    useEffect(() => {
        if (!id) return;
        const fetchCustomer = async () => {
            try {
                setLoading(true);
                const res = await customersAPI.getCustomer(id);
                setCustomer(res.data);
            } catch {
                toast.error('Failed to load customer');
                navigate(-1);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomer();
    }, [id]);

    const handleEditClick = () => {
        if (!customer) return;
        setFormData({
            firstName: customer.firstName,
            lastName: customer.lastName,
            nickname: customer.nickname || '',
            email: customer.email,
            phone: customer.phone || '',
            address: customer.address || '',
            postcode: customer.postcode || '',
            city: customer.city || '',
            isBusiness: customer.isBusiness || false,
            notes: customer.notes || '',
            gdprConsent: customer.gdprConsent || false,
            marketingConsent: customer.marketingConsent || false,
            preferredNotificationMethod: customer.preferredNotificationMethod || 'email',
        });
        setShowEditModal(true);
    };

    const updateCustomer = async () => {
        if (!customer) return;
        try {
            const response = await customersAPI.updateCustomer(customer._id, formData);
            setCustomer({ ...customer, ...response.data });
            setShowEditModal(false);
            toast.success('Customer updated successfully');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to update customer');
        }
    };

    const handleAddNote = async () => {
        if (!customer || !noteText.trim()) return;
        try {
            const response = await customersAPI.addCustomerNote(customer._id, noteText);
            setCustomer({ ...customer, notes: response.data.notes });
            setNoteText('');
            toast.success('Note added successfully');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to add note');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
        );
    }

    if (!customer) return null;

    const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Back
                        </button>
                        <button onClick={handleEditClick} className="flex items-center text-sm text-primary-600 hover:text-primary-700 transition-colors bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-md font-medium">
                            <Edit className="w-4 h-4 mr-1.5" /> Edit Customer
                        </button>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 h-14 w-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md">
                                <span className="text-white font-bold text-lg">
                                    {customer.firstName?.charAt(0)}{customer.lastName?.charAt(0)}
                                </span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{customer.firstName} {customer.lastName}</h1>
                                {customer.nickname && <p className="text-sm text-gray-500">Nickname: {customer.nickname} (internal only)</p>}
                                <p className="text-sm text-gray-500">Customer since {formatDate(customer.createdAt)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {!customer.isActive && (
                                <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactive</span>
                            )}
                            {customer.isBusiness && (
                                <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 items-center gap-1">
                                    <Building2 className="w-3 h-3" /> Business
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <ShoppingBag className="w-4 h-4 text-primary-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase">Total Orders</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{customer.totalOrders}</p>
                    </div>
                    <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="w-4 h-4 text-green-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase">Total Spent</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">£{customer.totalSpent?.toFixed(2)}</p>
                    </div>
                    <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase">Loyalty Points</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{customer.loyaltyPoints}</p>
                    </div>
                    <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase">Last Order</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{formatDate(customer.lastOrderDate)}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column — Order History */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-gray-400" /> Order History
                                    <span className="ml-auto text-sm font-normal text-gray-500">{customer.recentOrders?.length || 0} orders</span>
                                </h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Payment</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {customer.recentOrders?.map((order) => (
                                            <tr
                                                key={order._id}
                                                onClick={() => navigate(`/orders/${order._id}`)}
                                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <td className="px-5 py-3 text-sm font-medium text-primary-600">{order.orderNumber}</td>
                                                <td className="px-5 py-3 text-sm text-gray-500">{formatDate(order.createdAt)}</td>
                                                <td className="px-5 py-3 text-sm text-gray-700 text-right">£{order.totalAmount?.toFixed(2)}</td>
                                                <td className="px-5 py-3 text-center">
                                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                                                        {order.status?.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${paymentStatusColors[order.paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
                                                        {order.paymentStatus}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!customer.recentOrders || customer.recentOrders.length === 0) && (
                                            <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">No orders found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Right Column — Contact & Details */}
                    <div className="space-y-6">
                        {/* Contact Info */}
                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <User className="w-5 h-5 text-gray-400" /> Contact Info
                                </h2>
                            </div>
                            <div className="px-5 py-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="text-sm text-gray-700">{customer.phone || '—'}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="text-sm text-gray-700">{customer.email || '—'}</span>
                                </div>
                                {(customer.address || customer.city || customer.postcode) && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-gray-700">
                                            {customer.address && <p>{customer.address}</p>}
                                            {(customer.city || customer.postcode) && (
                                                <p>{[customer.city, customer.postcode].filter(Boolean).join(', ')}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {customer.dateOfBirth && (
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-700">{formatDate(customer.dateOfBirth)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Business Info */}
                        {customer.isBusiness && (
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-gray-400" /> Business Info
                                    </h2>
                                </div>
                                <div className="px-5 py-4 space-y-3">
                                    {customer.businessName && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Company</span><span className="text-gray-700">{customer.businessName}</span></div>
                                    )}
                                    {customer.vatNumber && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">VAT Number</span><span className="text-gray-700">{customer.vatNumber}</span></div>
                                    )}
                                    {customer.accountNumber && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Account #</span><span className="text-gray-700">{customer.accountNumber}</span></div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Account Info */}
                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-gray-400" /> Account
                                </h2>
                            </div>
                            <div className="px-5 py-4 space-y-3">
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Credit Limit</span><span className="text-gray-700">£{customer.creditLimit?.toFixed(2)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Balance</span><span className="text-gray-700">£{customer.currentBalance?.toFixed(2)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Notification</span><span className="text-gray-700 capitalize">{customer.preferredNotificationMethod}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">GDPR Consent</span><span className={customer.gdprConsent ? 'text-green-600' : 'text-red-600'}>{customer.gdprConsent ? 'Yes' : 'No'}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Marketing</span><span className={customer.marketingConsent ? 'text-green-600' : 'text-red-600'}>{customer.marketingConsent ? 'Opted In' : 'Opted Out'}</span></div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    Notes
                                </h2>
                            </div>
                            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        className="flex-1 border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="Add a new note..."
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                                    />
                                    <button
                                        onClick={handleAddNote}
                                        disabled={!noteText.trim()}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Add Note
                                    </button>
                                </div>
                            </div>
                            <div className="px-5 py-4">
                                {customer.notes ? (
                                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{customer.notes}</pre>
                                ) : (
                                    <p className="text-sm text-gray-500 italic">No notes have been added to this customer yet.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="fixed inset-0 overflow-y-auto z-50">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="sm:flex-auto">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Edit Customer
                                        </h3>
                                    </div>
                                </div>

                                <form className="mt-5 space-y-4" onSubmit={(e) => {
                                    e.preventDefault();
                                    updateCustomer();
                                }}>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                                            <input type="text" required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                                            <input type="text" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Email</label>
                                            <input type="email" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                                            <input type="tel" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Nickname (internal only)</label>
                                        <input type="text" maxLength={80} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.nickname} onChange={(e) => setFormData({ ...formData, nickname: e.target.value })} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Postcode</label>
                                            <input type="text" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.postcode} onChange={(e) => setFormData({ ...formData, postcode: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">City</label>
                                            <input type="text" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Address</label>
                                        <textarea rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700">
                                            <input type="checkbox" className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500" checked={formData.isBusiness} onChange={(e) => setFormData({ ...formData, isBusiness: e.target.checked })} />
                                            Is Business Customer
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-200">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Preferences & Consents</label>
                                            <div className="space-y-3">
                                                <label className="flex items-center text-sm font-medium text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                                                        checked={formData.gdprConsent}
                                                        onChange={(e) => setFormData({ ...formData, gdprConsent: e.target.checked })}
                                                    />
                                                    GDPR Consent
                                                </label>
                                                <label className="flex items-center text-sm font-medium text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                                                        checked={formData.marketingConsent}
                                                        onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                                                    />
                                                    Marketing Consent
                                                </label>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Notification Method</label>
                                                    <select
                                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md shadow-sm"
                                                        value={formData.preferredNotificationMethod}
                                                        onChange={(e) => setFormData({ ...formData, preferredNotificationMethod: e.target.value as any })}
                                                    >
                                                        <option value="email">Email</option>
                                                        <option value="sms">SMS</option>
                                                        <option value="both">Both</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3">
                                        <button type="button" onClick={() => setShowEditModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm">
                                            Cancel
                                        </button>
                                        <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm">
                                            Save Changes
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerDetail;
